/**
 * @file constants.ts
 * @module utils/constants
 * @description Application-wide constants and configuration values.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { getKey } from '../Global/history';
import { warehouse } from './storeString';
import { CONFIGURATION_SETTING_KEYS } from './configurationSettingKeys';

export const PREF_TOKEN = 'bearerToken';
export const IS_INTRO = '/';
export const IS_LOGIN = 'Login';
const DEFAULT_API_BASE_URL = 'https://api.utrade.local/';
const configuredApiBaseUrl =
  process.env.REACT_APP_BASE_URL || DEFAULT_API_BASE_URL;
export const API_BASE_URL = configuredApiBaseUrl.endsWith('/')
  ? configuredApiBaseUrl
  : `${configuredApiBaseUrl}/`;

export const PUBLIC_POSTHOG_KEY =
  'phc_OraPE8Dp18UvsaIJeKQxMPDyqBLlEHxvZkJCpFPV4wO';
export const PUBLIC_POSTHOG_HOST = 'https://eu.i.posthog.com';
export const POSTHOG_ENABLED_CACHE_KEY =
  CONFIGURATION_SETTING_KEYS.KEY__ENABLE_POSTHOG;
export const CONFIGURATION_RECORDS_CACHE_KEY = 'configurationRecordsCache';

export const PAGE_LIMIT = 10;
export const PAGE_LIMIT_MID = 50;
export const PAGE_LIMIT_LARGE = 100;
export const ORDER_PAGE_LIMIT = 100;
// API TYPES
export const GET = 'GET';
export const GET_URL_PARAMS = 'GET_URL_PARAMS';
export const GET_ID_PARAMS = 'GET_ID_PARAMS';
export const GET_URL_ENCODED = 'GET_URL_ENCODED';
export const GET_URL_ID_PARAMS = 'GET_URL_ID_PARAMS';
export const GET_URL_PARAMS_WITH_MULTIPLE_STATUS =
  'GET_URL_PARAMS_WITH_MULTIPLE_STATUS';
export const POST = 'POST';
export const POST_ID_PARAMS = 'POST_ID_PARAMS';
export const POST_RAW = 'POST_RAW';
export const POST_FORM = 'POST_FORM';
export const POST_URL_ENCODED = 'POST_URL_ENCODED';
export const POST_URL_PARAMS = 'POST_URL_PARAMS';
export const POST_URL_ENCODED_ID_PARAMS = 'POST_URL_ENCODED_ID_PARAMS';
export const MULTI_PART_POST = 'MULTI_PART';
export const PATCH = 'PATCH';
export const PATCH_ID = 'PATCH_ID';
export const PATCH_FORM = 'PATCH_FORM';
export const PATCH_FORM_ID = 'PATCH_FORM_ID';
export const PATCH_FORM_ID_V2 = 'PATCH_FORM_ID_V2';
export const PATCH_URL_ENCODED = 'PATCH_URL_ENCODED';
export const PATCH_FORM_ID_URL_ENCODED = 'PATCH_FORM_ID_URL_ENCODED';
export const MULTI_PART_ID_POST = 'MULTI_PART';
export const MULTI_PART_ID_PATCH = 'MULTI_PART_PATCH';
export const DELETE = 'DELETE';
export const DELETE_URL_PARAMS = 'DELETE_URL_PARAMS';
export const DELETE_URL_ENCODED = 'DELETE_URL_ENCODED';
export const DELETE_ID_PARAMS = 'DELETE_ID_PARAMS';
export const MULTI_PART_ID_DELETE = 'MULTI_PART_ID_DELETE';
//Response
export const ResponseFail = 400;
export const ResponseSuccess = 200;
export const AuthError = 401;
export const Maintenance = 503;
//KYC Request Enum
export const All = 1;
export const NewRequests = 2;
export const UnderReview = 3;
export const InfoRequired = 4;
export const Accepted = 5;
export const Rejected = 6;
//Technical Details Enum
export const SingleLineText = 1;
export const MultiLineText = 2;
export const SingleSelection = 3;
export const MultiSelection = 4;
export const DateSelection = 5;
//Variants Defined By Enum
export const Skip = 0;
export const SuggestOptions = 1;
export const TextGuide = 2;
//Product State Enum
export const AllProduct = 1;
export const Actived = 2;
export const Deactivated = 3;
export const VerificationPending = 4;
export const ProductRejectedState = 5;
//Quantity Enum
export const CartonWithDozens = 1;
export const Dozen = 2;
export const Piece = 3;
export const CartonWithPieces = 4;

// Stock update reasons enum used for quantity changes in review orders list
export enum StockUpdateReason {
  Missing = 'Missing',
  DamagedAtWarehouse = 'Damaged at Warehouse',
  Damaged = 'Damaged',
  Expired = 'Expired',
  NearExpiry = 'Near Expiry',
  Good = 'Good',
  WrongItemDispatched = 'Wrong Item Dispatched',
  WrongItemSelected = 'Wrong Item Selected',
  NoCash = 'No Cash',
  WrongItem = 'Wrong Item',
  EnrouteDamaged = 'Enroute Damaged',
  CustomerCancelled = 'Cancelled By Customer',
  MissingInventory = 'Missing from Inventory',
}
//Product Status Enum
export const ApprovedProduct = 2;
export const PendingProduct = 3;
export const RejectedProduct = 4;
// Orders Status
export const NewOrder = 1;
// export const PreparingForDispatch = 2;
export const Delivered = 3;
export const Cancelled = 4;
//Advertisement Type
export const HomePage = 1;
export const CategoryPage = 2;
//Advertisement Product Type
export const SingleProduct = 1;
export const MultipleProduct = 2;
//Advertisement State
export const AdvertisementRequest = 1;
export const AdvertisementAccepted = 2;
export const AdvertisementOngoing = 3;
export const AdvertisementUpcoming = 4;
export const AdvertisementCompleted = 5;
//Advertisement PaymentStatuses
export const AdvertisementPaid = 1;
export const AdvertisementUnpaid = 2;
//Advertisement PaymentMethods
export const Card = 1;
export const Cash = 2;
//Assist Admin User Role
export const Manager = 1;
export const ServiceMember = 2;
export const FinanceManager = 3;
export const LoadingBayManager = 4;
export const FulfillmentManager = 5;
export const ActivationManager = 6;
export const ActivationRep = 7;
//Admin UserType
export const Admin = 1;
export const AssistAdmin = 2;
//Permissions
export const AllPermissions = 0;
//Roles
// export const All = 0;
export const View = 1;
export const Add = 2;
export const Edit = 3;
export const Delete = 4;
export const Download = 5;
export const Dispatch = 6;
export const EditBypassPermission = 17;
export const LoadingBypassPermission = 18;
export const AddressEditPermission = 19;
export const OrderCancelPermission = 20;
export const RestrictOrderViewPermission = 29;
export const ViewRoutePermission = 30;
export const CancelDeliveryPlanPermission = 31;
export const DownloadGRDocumentPermission = 32;
export const DownloadOrderDocumentPermission = 33;
export const UtradeDiscountPermission = 34;
export const ChangeDriverPermission = 35;
export const ChangeLoadingManagerPermission = 36;

/** use this `Permissions` enum for accessing all the Permissions `value` */
export enum Permissions {
  AllPermissions = 0,
  View = 1,
  Add = 2,
  Edit = 3,
  Delete = 4,
  Download = 5,
  Dispatch = 6,
  Pending_Tab = 7,
  Approved_Tab = 8,
  In_Process_Tab = 9,
  Completed_Tab = 10,
  Rejected_Tab = 11,
  Cancelled_Tab = 12,
  PO_DELIVERY_DATE_UPDATE = 13,
  PO_CREATE_GRN = 14,
  PO_APPROVE_AND_REJECT = 15,
  PO_SAVE_ALL = 16,
  EditBypass = 17,
  LoadingBypass = 18,
  AddressEdit = 19,
  OrderCancel = 20,
  PC_CriticalAlerts = 21,
  PC_ChangeHistory = 22,
  PC_EditCostPrice = 23,
  PC_EditSellingPrice = 24,
  PC_EditTax = 25,
  PC_EditDaysOfInventoryHolding = 26,
  PC_EditDraftPOenabled = 27,
  PC_All_Warehouses = 28,
  RestrictOrderView = 29,
  ViewRoute = 30,
  CancelDeliveryPlan = 31,
  DownloadGRDocument = 32,
  DownloadOrderDocument = 33,
  UtradeDiscount = 34,
  ChangeDriverPermission = 35,
  ChangeLoadingManagerPermission = 36,
  OrdersDelivery_Mark_goods_as_loaded = 37,
  OrdersDelivery_Mark_as_out_for_delivery = 38,
  // 39-49 kept free for other feature branches
  Draft_PO_Tab = 50,
  Draft_PO_Edit = 51,
  Draft_PO_Delete = 52,
  Draft_PO_Bypass_Tonnage = 53,
  Draft_PO_Bypass_MOQ = 54,
  Supplier_Tonnage_Edit = 55,
  Supplier_MOQ_Edit = 56,
  Supplier_Groups_Edit = 57,
  // Inward module (moved from 37-42 to 70-75: main uses 37/38 for Orders & Delivery)
  /** No longer used: GRNs are created only by the PO's assigned loading bay manager (or super admin). */
  InwardCreateGRN = 70,
  InwardLoadingBayManager = 71,
  InwardWarehouseApprove = 72,
  InwardFinanceApprove = 73,
  InwardAssignLoadingBay = 74,
  /** Acknowledge a Finance cost price change (Tier 1 price catalog alert). */
  InwardAcknowledgeCostPrice = 75,
  /** Tick "EFD verified" on a GRN in the Inward report (a person checked the EFD / TRA receipt). */
  InwardVerifyEfd = 76,
  /** Reject a whole Inward GRN: its quantities go back to the PO for a new GRN. */
  InwardRejectGrn = 77,
}

//Permissions module
//? use this PermissionModule enum instead single exported constants
export enum PermissionModule {
  AllModules = 0,
  Sellers = 1,
  AllSeller = 2,
  KycVerifications = 3,
  PendingCertificate = 4,
  PaymentToSellers = 5,
  SellersSalesReport = 6,
  Customers = 7,
  AllCustomers = 8,
  RefundsToCustomer = 9,
  ReceivableReports = 10,
  OrdersDelivery = 11,
  RoutePlan = 12,
  Master = 13,
  Categories = 14,
  BusinessType = 15,
  BusinessCertificates = 16,
  Brand = 17,
  TechnicalDetails = 18,
  ProductVariants = 19,
  MasterProduct = 20,
  Driver = 21,
  Vehicle = 22,
  VehicleLoadingArea = 23,
  SellerProductZone = 24,
  FinanceMember = 25,
  RolesAndPermission = 26,
  UserManagement = 27,
  Inventory = 28,
  GoodsWarehouse = 29,
  LowStockLists = 30,
  GoodsRequest = 31,
  ReceivedDirectGood = 32,
  CustomerServices = 33,
  All_Products = 34,
  PlatformFees = 35,
  Advertising = 36,
  ContactInquires = 37,
  CustomNotifications = 38,
  Settings = 39,
  Privacy_Policy = 40,
  TermsConditions = 41,
  AppSettings = 42,
  Dashboard = 43,
  FinanceReports = 44,
  OutWardReports = 45,
  ReturnProductReports = 46,
  DeliveryReports = 47,
  Reports = 48,
  CustomerPurchaseBehaviourReport = 49,
  GeographicSalesInsightReport = 50,
  AreaBasedCustomerSegmentationReport = 51,
  FrequentCustomerPurchasePatternAnalysisReport = 52,
  ProductSalesPerformanceReport = 53,
  BrandWiseSalesPerformanceReport = 54,
  CategoryAndSubCategoryAnalysisReport = 55,
  RevenueGenerationReport = 56,
  CommissionAnalysisReport = 57,
  InventoryStatusReport = 58,
  InterTransferUmart = 59,
  PreOrderCustomerList = 60,
  WarehouseModule = 61,
  WarehouseManger = 62,
  WarehouseInterTransfer = 63,
  purchaseOrder = 64,
  // here below add quotation module in future
  PendingDeliveryOrder = 65,
  OutOfStockList = 66,
  OutstandingReport = 67,
  DriverLedger = 68,
  DistrictAndCity = 69,
  CustomerLedger = 70,
  PriceUpdateRequest = 71,
  RouteProductReports = 72,
  POManagement = 73,
  //vendors
  AllVendors = 74,
  PaymentToVendors = 75,
  AssignProductToVendors = 76,
  GoodsReceivedNote = 77,
  Escalations = 78,
  VendorLedger = 79,
  // Sales Management
  OutOfStockReportPermission = 80,
  /** same module as OutOfStockReportPermission: existing roles keep their access */
  InventoryReplenishmentReport = 80,
  SalesDashboard = 81,
  SalesRepresentative = 82,
  CustomerAssignment = 83,
  TargetManagement = 84,
  SalesRepOrders = 85,
  SalesAlerts = 86,
  FinanceSalesReportPermission = 87,
  FinanceReturnReportPermission = 88,
  FinanceDeliveryReportPermission = 89,
  ProductReturnRequests = 90,
  UProcure = 91,
  UProcureDashboard = 92,
  InventoryReportPermission = 93,
  FinanceOrderReportPermission = 94,
  ActivityLogs = 95,
  PriceCatalog = 96,
  DispatchReport = 97,
  DispatchReportOnlyReturnConsidered = 98,
  DispatchReportWithReturnedCombined = 99,
  // Inward (moved from 97: main uses 97 for Dispatch Report)
  Inward = 102,
}

//! do not use below exports use PermissionModule enum below exports will be removed in future
export const AllModules = PermissionModule.AllModules;
export const Sellers = PermissionModule.Sellers;
export const AllSeller = PermissionModule.AllSeller;
export const KycVerifications = PermissionModule.KycVerifications;
export const PendingCertificate = PermissionModule.PendingCertificate;
export const PaymentToSellers = PermissionModule.PaymentToSellers;
export const SellersSalesReport = PermissionModule.SellersSalesReport;
export const Customers = PermissionModule.Customers;
export const AllCustomers = PermissionModule.AllCustomers;
export const RefundsToCustomer = PermissionModule.RefundsToCustomer;
export const ReceivableReports = PermissionModule.ReceivableReports;
export const OrdersDelivery = PermissionModule.OrdersDelivery;
export const RoutePlan = PermissionModule.RoutePlan;
export const Master = PermissionModule.Master;
export const Categories = PermissionModule.Categories;
export const BusinessType = PermissionModule.BusinessType;
export const BusinessCertificates = PermissionModule.BusinessCertificates;
export const Brand = PermissionModule.Brand;
export const TechnicalDetails = PermissionModule.TechnicalDetails;
export const ProductVariants = PermissionModule.ProductVariants;
export const MasterProduct = PermissionModule.MasterProduct;
export const Driver = PermissionModule.Driver;
export const ActivityLogs = PermissionModule.ActivityLogs;
export const Vehicle = PermissionModule.Vehicle;
export const VehicleLoadingArea = PermissionModule.VehicleLoadingArea;
export const SellerProductZone = PermissionModule.SellerProductZone;
export const FinanceMember = PermissionModule.FinanceMember;
export const RolesAndPermission = PermissionModule.RolesAndPermission;
export const UserManagement = PermissionModule.UserManagement;
export const Inventory = PermissionModule.Inventory;
export const GoodsWarehouse = PermissionModule.GoodsWarehouse;
export const LowStockLists = PermissionModule.LowStockLists;
export const GoodsRequest = PermissionModule.GoodsRequest;
export const ReceivedDirectGood = PermissionModule.ReceivedDirectGood;
export const CustomerServices = PermissionModule.CustomerServices;
export const All_Products = PermissionModule.All_Products;
export const PlatformFees = PermissionModule.PlatformFees;
export const Advertising = PermissionModule.Advertising;
export const ContactInquires = PermissionModule.ContactInquires;
export const CustomNotifications = PermissionModule.CustomNotifications;
export const Settings = PermissionModule.Settings;
export const Privacy_Policy = PermissionModule.Privacy_Policy;
export const TermsConditions = PermissionModule.TermsConditions;
export const AppSettings = PermissionModule.AppSettings;
export const Dashboard = PermissionModule.Dashboard;
export const FinanceReports = PermissionModule.FinanceReports;
export const OutWardReports = PermissionModule.OutWardReports;
export const ReturnProductReports = PermissionModule.ReturnProductReports;
export const DeliveryReports = PermissionModule.DeliveryReports;
export const Reports = PermissionModule.Reports;
export const CustomerPurchaseBehaviourReport =
  PermissionModule.CustomerPurchaseBehaviourReport;
export const GeographicSalesInsightReport =
  PermissionModule.GeographicSalesInsightReport;
export const AreaBasedCustomerSegmentationReport =
  PermissionModule.AreaBasedCustomerSegmentationReport;
export const FrequentCustomerPurchasePatternAnalysisReport =
  PermissionModule.FrequentCustomerPurchasePatternAnalysisReport;
export const ProductSalesPerformanceReport =
  PermissionModule.ProductSalesPerformanceReport;
export const BrandWiseSalesPerformanceReport =
  PermissionModule.BrandWiseSalesPerformanceReport;
export const CategoryAndSubCategoryAnalysisReport =
  PermissionModule.CategoryAndSubCategoryAnalysisReport;
export const RevenueGenerationReport = PermissionModule.RevenueGenerationReport;
export const CommissionAnalysisReport =
  PermissionModule.CommissionAnalysisReport;
export const InventoryStatusReport = PermissionModule.InventoryStatusReport;
export const InterTransferUmart = PermissionModule.InterTransferUmart;
export const PreOrderCustomerList = PermissionModule.PreOrderCustomerList;
export const WarehouseModule = PermissionModule.WarehouseModule;
export const WarehouseManger = PermissionModule.WarehouseManger;
export const WarehouseInterTransfer = PermissionModule.WarehouseInterTransfer;
export const PendingDeliveryOrder = PermissionModule.PendingDeliveryOrder;
export const OutOfStockList = PermissionModule.OutOfStockList;
export const OutstandingReport = PermissionModule.OutstandingReport;
export const DriverLedger = PermissionModule.DriverLedger;
export const DistrictAndCity = PermissionModule.DistrictAndCity;
export const CustomerLedger = PermissionModule.CustomerLedger;
export const PriceUpdateRequest = PermissionModule.PriceUpdateRequest;
export const RouteProductReports = PermissionModule.RouteProductReports;
export const POManagement = PermissionModule.POManagement;
export const AllVendors = PermissionModule.AllVendors;
export const PaymentToVendors = PermissionModule.PaymentToVendors;
export const AssignProductToVendors = PermissionModule.AssignProductToVendors;
export const GoodsReceivedNote = PermissionModule.GoodsReceivedNote;
export const Escalations = PermissionModule.Escalations;
export const VendorLedger = PermissionModule.VendorLedger;
export const OutOfStockReportPermission =
  PermissionModule.OutOfStockReportPermission;
export const InventoryReplenishmentReport =
  PermissionModule.InventoryReplenishmentReport;
export const SalesDashboard = PermissionModule.SalesDashboard;
export const SalesRepresentative = PermissionModule.SalesRepresentative;
export const CustomerAssignment = PermissionModule.CustomerAssignment;
export const TargetManagement = PermissionModule.TargetManagement;
export const SalesRepOrders = PermissionModule.SalesRepOrders;
export const SalesAlerts = PermissionModule.SalesAlerts;
export const FinanceSalesReportPermission =
  PermissionModule.FinanceSalesReportPermission;
export const FinanceReturnReportPermission =
  PermissionModule.FinanceReturnReportPermission;
export const FinanceDeliveryReportPermission =
  PermissionModule.FinanceDeliveryReportPermission;
export const ProductReturnRequests = PermissionModule.ProductReturnRequests;
export const UProcure = PermissionModule.UProcure;
export const UProcureDashboard = PermissionModule.UProcureDashboard;
export const InventoryReportPermission =
  PermissionModule.InventoryReportPermission;
export const purchaseOrder = PermissionModule.purchaseOrder;

//export const Product = 3;
// Modules
// export const All = 0;
// export const Product = 3;
//OrderType
export const ReturnInitiated = 1;
export const PreparingForDispatch = 2;
export const OutForDelivery = 3;
export const CollectToday = 4;
//Inventory Transaction/History
export const ShipmentThroughAdded = 'c1'; //New Stock Updated
export const AddedOnReturn = 'c2'; //Customer return
export const ManuallyAdded = 'c3'; //New Stock Updated
export const DirectlyReceived = 'c4'; //New Stock Updated
export const AddedOnOrderCancellation = 'c5'; //Order modified
export const AddedOnOrderModification = 'c6'; //Order modified
export const AddedOnConversion = 'c7'; //Order modified
export const AddedInterTransferRequest = 'c8';
export const DeductedOnSale = 'd1'; //New order
export const DeductedOnReturnToSeller = 'd2'; //Goods return request
export const ManuallyDeducted = 'd3'; //Missing/Damaged goods
export const DeductedOnMissing = 'd4'; //Missing/Damaged goods
export const DeductedOnDamage = 'd5'; //Missing/Damaged goods
export const DeductedOnOrderModification = 'd6'; //Order modified
export const DeductedOnConversion = 'd7'; //Order modified
export const TransferRequest = 'd8';
export const EnrouteDamaged = 'd9';
export const Expired = 'd10';
export const DamagedAtWarehouse = 'd11';
//Shipment Status
export const BulkShipmentSubmitted = 0;
export const ShipmentSubmitted = 1;
export const ShipmentAccepted = 2;
export const ShipmentCancelled = 3;
export const ShipmentRejected = 4;
export const ShipmentPickedUp = 5;
export const ShipmentDelivered = 6;
export const ShipmentCompleted = 7;
export const ShipmentPickedUpStarted = 8;
export const ShipmentPartialDelivered = 9;
//User Types
export const SuperAdmin = 2;
export const Seller = 1;
//Shipment Type
export const SelfTransport = 1;
export const UTradeTransport = 2;
//Shipment Fees
export const WarehouseStorage = 1;
export const Shipping = 2;
//Order Status
export const OrderSubmitted = 1; //New-order-placed
export const OrderProcessed = 2; //Route-planning generated
export const OrderOutForDelivery = 3; //out for delivery
export const OrderDelivered = 4; //Delivered
export const OrderCancelled = 5; //Cancelled
//Tax Types
export const GeneralTax = 1;
export const SystemTax = 2;
//Order Payment method
export const OrderCash = 1;
export const OrderTigoPesa = 2;
export const OrderMPesa = 3;
export const OrderCard = 4;
export const DTBBank = 5;
export const CRDBBank = 6;
export const NMBBank = 7;
export const StanbicBank = 8;
export const AirtelMoney = 9;
export const HaloPesa = 10;
//Google autocomplete apikey
export const APIkey = 'AIzaSyAg41o-JrOGvOov4uDRb9CzL2AyfUMYDbI';
//Route Planning
export const RoutePlanning = 1;
export const RoutePlanned = 2;
export const RoutePlanCompleted = 3;
export const RoutePlanCancelled = 4;
export const RoutePlanFailed = 5;
export const RoutePlanScheduled = 6;
//Route Plan Vehicle
export const RouteOrdersAssigned = 1;
export const RouteOrderLoadingInitiated = 2;
export const RouteOrderOutForDelivery = 3;
export const RouteOrderDelivered = 4;
export const RouteOrderCancelled = 5; //End-status
// Keys must match RoutePlanCancellationReasons in the uTrade backend (_product/utils/constants.js)
export const ROUTE_PLAN_CANCELLATION_REASONS = [
  { key: 'UNDER_WEIGHT', label: 'Route plan under weight' },
  { key: 'CREDIT_CUSTOMER', label: 'Credit customer' },
  { key: 'CANCELLED_BY_CUSTOMER', label: 'Cancelled by customer' },
  { key: 'CUSTOMER_WANTS_LATER', label: 'Customer wants later' },
  { key: 'WRONG_WARD', label: 'Wrong ward' },
  { key: 'OTHER', label: 'Other' },
];
export const OTHER_CANCELLATION_REASON_KEY = 'OTHER';
//Route Plan Vehicle Order
export const RouteOrderNotDispatched = 1; //In progress
export const RouteOrderDispatched = 2; //Out for delivery
export const RouteVehicleOrderDelivered = 3; //Delivered
export const RouteOrderCustomerNotAvailable = 4; //Customer not available
export const RouteOrderMovedToPendingOrder = 5; //Move to pending
//Route Plan Payment Collection
export const RouteOrderPaymentNONE = 1;
export const RouteOrderPaymentFULL = 2;
//Route Plan Order Loading
export const RouteOrderNotLoaded = 1;
export const RouteOrderLoaded = 2;
export const RouteOrderVariantRemoved = 3;
//Route Plan Payment Method
export const RouteOrderCash = 1;
export const RouteOrderTigoPesa = 2;
//Order placed by types
export const OrderPlacedByCustomer = 1;
export const OrderPlacedByAdmin = 2;
export const OrderPlacedByAssistance = 3;
//Seller Payment Status
export const SellerPaymentPaid = 1;
export const SellerPaymentUnPaid = 2;
//Seller Payment Method
export const BankPayment = 1;
export const TigoPesaPayment = 2;
export const Credit = 3;
// Delivery Payment Method
export const AllPaymentMethod = 0;
export const DeliveryCash = 1;
export const DeliveryTigoPesa = 2;
export const DeliveryBank = 3;
//Notification Type
export const General = 1;
export const Product = 2;
export const Cart = 3;
export const GuestUser = 4;
export const ActiveUser = 5;
//Admin Notification Type
export const BuyerContact = 1;
export const SellerContact = 2;
export const MarkRouteAsCompleted = 3;
export const Escalation = 16;
//Customer Filter Type
export const Registered = 1;
export const NotRegistered = 0;
//Order Queue Status
export const OrderQueueSubmitted = 1; //Added to Queue
export const OrderQueueProcessing = 2; //Processing
export const OrderQueueProcessed = 3; //Processed
export const OrderQueueFailed = 4; //Failed Due to Error

//Transfer Request Status
export const TransferRequestSubmitted = 1;
export const TransferRequestAccepted = 2;
export const TransferRequestCancelled = 3;
export const TransferRequestRejected = 4;
export const TransferRequestPickup = 5;
export const TransferRequestDelivered = 6;
export const TransferRequestCompleted = 7;
export const TransferRequestPickupStarted = 8;

//Price Request Status
export const PriceRequestSubmitted = 1;
export const PriceRequestAccepted = 2;
export const PriceRequestRejected = 3;

export const warehouseRef = (() => {
  const storedStatus = getKey(warehouse.warehouseRef);
  if (typeof storedStatus === 'string') {
    return JSON.parse(storedStatus);
  }
  return storedStatus ?? null;
})();
//Payment Status
export const PaymentStatusAll = 1;
export const PaymentStatusPending = 2;
export const PaymentStatusPartiallyPaid = 3;
export const PaymentStatusFullyPaid = 4;

//Receipt Status
export const ReceiptStatusAll = 1;
export const ReceiptStatusPending = 4;
export const ReceiptStatusCompleted = 3;
//Sales-rep Alerts
export const InActiveCustomerAlerts = 19;
export const LostOrderAlerts = 20;
// Product Return Status
export const ReturnProductsRequestInitiated = 1; //When return initiate by delivery user or admin
export const ReturnProductsRequestRejected = 2; //Status rejected by Finance
export const ReturnProductsRequestApproved = 3; //Status approved by Finance
